import { useDocumentUpload } from '@/hooks/useDocumentUpload';
import { supabase } from '@/lib/supabase';
import { LinearGradient } from 'expo-linear-gradient';
import { router } from 'expo-router';
import { Camera, Cards, File, FileText, Images, Question, X } from 'phosphor-react-native';
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Animated, Dimensions, Image, KeyboardAvoidingView, Modal, Platform, ScrollView, StyleSheet, TextInput, TouchableOpacity, View } from 'react-native';
import { ActivityIndicator, Text } from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

// ✅ Détection plateforme web
const isWeb = Platform.OS === 'web';
const MAX_WIDTH = 500;

// ✅ Hauteur de l'écran pour adapter le modal
const { height: SCREEN_HEIGHT } = Dimensions.get('window');

// Design System Colors
const colors = {
  indigo700: '#4F46E5',
  indigo600: '#6366F1',
  indigo500: '#818CF8',
  indigo200: '#C7D2FE',
  indigo100: '#E0E7FF',
  indigo50: '#EEF2FF',
  slate900: '#0F172A',
  slate700: '#334155',
  slate600: '#475569',
  slate500: '#64748B',
  slate400: '#94A3B8',
  slate300: '#CBD5E1',
  slate200: '#E2E8F0',
  slate100: '#F1F5F9',
  slate50: '#F8FAFC',
  white: '#FFFFFF',
  purple600: '#7C3AED',
  purple100: '#EDE9FE',
  amber500: '#F59E0B',
  amber100: '#FEF3C7',
  emerald500: '#10B981',
  emerald100: '#D1FAE5',
  rose500: '#F43F5E',
  rose100: '#FFE4E6',
};

type ContentIntent = 'fiche' | 'quiz' | 'flashcards';

// ✅ XP par action
const XP_VALUES = {
  fiche: 10,
  quiz: 20,
  flashcards: 10,
};

// ✅ Limites FREE
const FREE_LIMITS = {
  fiches: 5,
  quiz: 5,
  flashcards: 5,
};

interface MonthlyUsage {
  fiches: number;
  quiz: number;
  flashcards: number;
}

// ✅ Définition des étapes de génération
interface GenerationStep {
  id: number;
  label: string;
  status: 'pending' | 'active' | 'completed';
}

// ═══════════════════════════════════════════════════════════════
// ✅ CORRIGÉ : Répartition équilibrée des 4 étapes sur le vrai temps
// Étape 1 = Upload (vérification, upload, création) → 0-25%
// Étape 2 = Analyse (extraction du texte) → 25-50%
// Étape 3 = Rédaction (génération IA) → 50-85%
// Étape 4 = Finalisation (XP, terminé) → 85-100%
// ═══════════════════════════════════════════════════════════════

const getStepsFromProgress = (progress: string, intent: ContentIntent): GenerationStep[] => {
  const contentLabel = intent === 'fiche' ? 'la fiche' : 
                       intent === 'quiz' ? 'le quiz' : 
                       'les flashcards';
  
  const steps: GenerationStep[] = [
    { id: 1, label: 'Upload du document', status: 'pending' },
    { id: 2, label: 'Analyse du contenu', status: 'pending' },
    { id: 3, label: `Rédaction de ${contentLabel}`, status: 'pending' },
    { id: 4, label: 'Finalisation', status: 'pending' },
  ];

  const progressLower = progress.toLowerCase();
  
  if (progressLower.includes('vérification') || progressLower.includes('upload') || progressLower.includes('création')) {
    // Étape 1 active : Upload en cours
    steps[0].status = 'active';
  } else if (progressLower.includes('extraction') || progressLower.includes('traitement') || progressLower.includes('fusion')) {
    // Étape 2 active : Analyse du texte
    steps[0].status = 'completed';
    steps[1].status = 'active';
  } else if (progressLower.includes('génération')) {
    // Étape 3 active : Génération IA (la plus longue)
    steps[0].status = 'completed';
    steps[1].status = 'completed';
    steps[2].status = 'active';
  } else if (progressLower.includes('attribution') || progressLower.includes('xp')) {
    // Étape 4 active : Finalisation
    steps[0].status = 'completed';
    steps[1].status = 'completed';
    steps[2].status = 'completed';
    steps[3].status = 'active';
  } else if (progressLower.includes('terminé')) {
    // Tout terminé
    steps[0].status = 'completed';
    steps[1].status = 'completed';
    steps[2].status = 'completed';
    steps[3].status = 'completed';
  }

  return steps;
};

// ✅ Mapper le progress vers un % cible (réparti selon le vrai temps)
const getTargetPercent = (progress: string): number => {
  const p = progress.toLowerCase();
  if (p.includes('terminé')) return 100;
  if (p.includes('attribution') || p.includes('xp')) return 92;
  if (p.includes('génération')) return 55;  // la génération IA est longue, on commence à 55%
  if (p.includes('extraction') || p.includes('traitement') || p.includes('fusion')) return 30;
  if (p.includes('création')) return 22;
  if (p.includes('upload')) return 12;
  if (p.includes('vérification')) return 5;
  return 0;
};

interface CreateContentModalProps {
  visible: boolean;
  onClose: () => void;
  initialIntent?: ContentIntent;
  monthlyUsage?: MonthlyUsage;
  isPremium?: boolean;
}

interface GeneratedContent {
  noteId?: string;
  quizId?: string;
  flashcardSetId?: string;
}

export default function CreateContentModal({ 
  visible, 
  onClose, 
  initialIntent = 'fiche',
  monthlyUsage = { fiches: 0, quiz: 0, flashcards: 0 },
  isPremium = false,
}: CreateContentModalProps) {
  const insets = useSafeAreaInsets();
  
  const [selectedIntent, setSelectedIntent] = useState<ContentIntent>(initialIntent);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showPhotoMode, setShowPhotoMode] = useState(false);
  const [showRenameStep, setShowRenameStep] = useState(false);
  const [customTitle, setCustomTitle] = useState('');
  const [generatedContent, setGeneratedContent] = useState<GeneratedContent | null>(null);
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [xpData, setXpData] = useState<{
    xpAwarded: number;
    newStreak: number;
    leveledUp: boolean;
    feedbackMessage: string;
  } | null>(null);

  // ✅ Pourcentage affiché (avance progressivement vers le target)
  const [displayPercent, setDisplayPercent] = useState(0);
  const targetPercentRef = useRef(0);
  const percentIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const scaleAnim = React.useRef(new Animated.Value(0)).current;
  const opacityAnim = React.useRef(new Animated.Value(0)).current;
  
  const {
    isLoading,
    progress,
    error,
    photos,
    addPhoto,
    removePhoto,
    clearPhotos,
    pickImage,
    pickDocument,
    uploadAndGenerate,
    uploadMultipleAndGenerate,
    resetState,
  } = useDocumentUpload();

  useEffect(() => {
    if (visible) {
      setSelectedIntent(initialIntent);
      setShowSuccess(false);
      setShowPhotoMode(false);
      setShowRenameStep(false);
      setCustomTitle('');
      setGeneratedContent(null);
      setXpData(null);
      setDisplayPercent(0);
      targetPercentRef.current = 0;
      clearPhotos();
    }
  }, [visible, initialIntent]);

  useEffect(() => {
    if (showSuccess) {
      Animated.parallel([
        Animated.spring(scaleAnim, {
          toValue: 1,
          friction: 5,
          tension: 40,
          useNativeDriver: true,
        }),
        Animated.timing(opacityAnim, {
          toValue: 1,
          duration: 300,
          useNativeDriver: true,
        }),
      ]).start();
    } else {
      scaleAnim.setValue(0);
      opacityAnim.setValue(0);
    }
  }, [showSuccess]);

  // ✅ Mettre à jour le target% quand le progress change
  useEffect(() => {
    targetPercentRef.current = getTargetPercent(progress);
  }, [progress]);

  // ✅ Animation fluide du pourcentage : avance doucement vers le target
  useEffect(() => {
    if (isLoading) {
      setDisplayPercent(0);
      targetPercentRef.current = 0;
      
      percentIntervalRef.current = setInterval(() => {
        setDisplayPercent(prev => {
          const target = targetPercentRef.current;
          if (prev >= target) {
            // Si on a atteint le target, avancer très lentement pour simuler le travail
            // (sauf si on est à 100%)
            if (prev >= 100) return 100;
            if (target >= 92) return Math.min(prev + 0.3, 99); // presque fini, avance doucement
            return Math.min(prev + 0.2, target + 5); // petit dépassement max de 5% au-delà du target
          }
          // Avancer vers le target : plus rapide quand on est loin
          const diff = target - prev;
          const increment = Math.max(0.5, diff * 0.08);
          return Math.min(prev + increment, target);
        });
      }, 200);
    } else {
      if (percentIntervalRef.current) {
        clearInterval(percentIntervalRef.current);
        percentIntervalRef.current = null;
      }
      // Si terminé, snap à 100%
      if (progress.toLowerCase().includes('terminé')) {
        setDisplayPercent(100);
      }
    }
    return () => {
      if (percentIntervalRef.current) {
        clearInterval(percentIntervalRef.current);
      }
    };
  }, [isLoading]);

  const handleClose = () => {
    if (!isLoading && !isSavingTitle) {
      resetState();
      setShowSuccess(false);
      setShowPhotoMode(false);
      setShowRenameStep(false);
      setCustomTitle('');
      setGeneratedContent(null);
      setXpData(null);
      setDisplayPercent(0);
      onClose();
    }
  };

  const handleAddPhoto = async (useCamera: boolean) => {
    if (useCamera) {
      const uri = await pickImage(true);
      if (uri) {
        addPhoto(uri, `photo_${Date.now()}.jpg`);
      }
    } else {
      await pickImage(false);
    }
  };

  const redirectToPaywall = () => {
    handleClose();
    setTimeout(() => {
      router.push('/paywall');
    }, 300);
  };

  const showErrorAlert = (errorMessage: string, errorCode?: string) => {
    const isPremiumDailyLimit = errorCode === 'PREMIUM_DAILY_LIMIT';
    const isFreeLimitReached = errorCode === 'FREE_LIMIT_REACHED' || (!isPremiumDailyLimit && errorMessage?.toLowerCase().includes('limite'));
    const isFileTooLarge = errorMessage?.startsWith('FILE_TOO_LARGE:');
    
    let title = 'Erreur';
    let message = errorMessage || 'Une erreur est survenue';
    
    if (isPremiumDailyLimit) {
      title = 'Limite journalière atteinte';
      message = message.replace(' 📚', '').replace('📚', '');
    } else if (isFreeLimitReached) {
      title = 'Limite atteinte';
      message = message.replace(' 🚀', '').replace('🚀', '');
    } else if (isFileTooLarge) {
      const fileSizeMB = errorMessage.split(':')[1] || '?';
      title = 'Fichier trop volumineux';
      message = `Ton document fait ${fileSizeMB} Mo, mais la limite est de 2 Mo.\n\nPour de meilleurs résultats, utilise un fichier de 10 pages max.\n\nAstuce : découpe ton cours en plusieurs parties !`;
    }
    
    if (isWeb) {
      window.alert(`${title}\n\n${message}`);
      if (isFreeLimitReached) {
        redirectToPaywall();
      }
    } else {
      if (isPremiumDailyLimit) {
        Alert.alert(
          title,
          message,
          [{ text: 'Compris', style: 'default', onPress: handleClose }]
        );
      } else if (isFreeLimitReached) {
        Alert.alert(
          title,
          message,
          [
            { 
              text: 'Passer Premium', 
              style: 'default', 
              isPreferred: true,
              onPress: redirectToPaywall 
            },
            { text: 'Plus tard', style: 'cancel', onPress: handleClose },
          ]
        );
      } else {
        Alert.alert(title, message);
      }
    }
  };

  const showAlert = (title: string, message: string) => {
    if (isWeb) {
      window.alert(`${title}\n\n${message}`);
    } else {
      Alert.alert(title, message);
    }
  };

  const getDefaultTitle = () => {
    const now = new Date();
    const dateStr = now.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    switch (selectedIntent) {
      case 'fiche': return `Fiche du ${dateStr}`;
      case 'quiz': return `Quiz du ${dateStr}`;
      case 'flashcards': return `Flashcards du ${dateStr}`;
    }
  };

  const handleGenerationSuccess = (result: any) => {
    setXpData({
      xpAwarded: result.xpAwarded || 0,
      newStreak: result.newStreak || 0,
      leveledUp: result.leveledUp || false,
      feedbackMessage: result.feedbackMessage || 'Bien joué !',
    });
    
    setGeneratedContent({
      noteId: result.noteId,
      quizId: result.quizId,
      flashcardSetId: result.flashcardSetId,
    });
    
    setCustomTitle(getDefaultTitle());
    setShowRenameStep(true);
  };

  const saveTitle = async () => {
    if (!generatedContent || !customTitle.trim()) return;
    
    setIsSavingTitle(true);
    
    try {
      const title = customTitle.trim();
      
      if (generatedContent.noteId) {
        await supabase
          .from('notes')
          .update({ title })
          .eq('id', generatedContent.noteId);
      } else if (generatedContent.quizId) {
        await supabase
          .from('quizzes')
          .update({ title })
          .eq('id', generatedContent.quizId);
      } else if (generatedContent.flashcardSetId) {
        await supabase
          .from('flashcard_sets')
          .update({ title })
          .eq('id', generatedContent.flashcardSetId);
      }
      
      setShowRenameStep(false);
      setShowSuccess(true);
      
      setTimeout(() => {
        handleClose();
        
        if (generatedContent.noteId) {
          router.push(`/notes/${generatedContent.noteId}`);
        } else if (generatedContent.quizId) {
          router.push(`/quiz/${generatedContent.quizId}`);
        } else if (generatedContent.flashcardSetId) {
          router.push(`/flashcards/${generatedContent.flashcardSetId}`);
        }
      }, 2000);
    } catch (error) {
      console.error('Error saving title:', error);
      showAlert('Erreur', 'Impossible de sauvegarder le titre');
    } finally {
      setIsSavingTitle(false);
    }
  };

  const skipRename = () => {
    setShowRenameStep(false);
    setShowSuccess(true);
    
    setTimeout(() => {
      handleClose();
      
      if (generatedContent?.noteId) {
        router.push(`/notes/${generatedContent.noteId}`);
      } else if (generatedContent?.quizId) {
        router.push(`/quiz/${generatedContent.quizId}`);
      } else if (generatedContent?.flashcardSetId) {
        router.push(`/flashcards/${generatedContent.flashcardSetId}`);
      }
    }, 2000);
  };

  const handleGenerateFromPhotos = async () => {
    if (photos.length === 0) {
      showAlert('Attention', 'Ajoute au moins une photo');
      return;
    }

    const result = await uploadMultipleAndGenerate(photos, selectedIntent);

    if (result.success) {
      handleGenerationSuccess(result);
    } else {
      showErrorAlert(result.error || 'Une erreur est survenue', result.errorCode);
    }
  };

  const handleSourceSelect = async (source: 'photo' | 'document') => {
    if (source === 'photo') {
      setShowPhotoMode(true);
      return;
    }

    const result = await pickDocument();
    const fileName = `document_${Date.now()}.pdf`;
    
    if (result.error?.startsWith('FILE_TOO_LARGE:')) {
      const fileSizeMB = result.error.split(':')[1] || '?';
      
      if (isWeb) {
        window.alert(
          `📄 Fichier trop volumineux\n\n` +
          `Ton document fait ${fileSizeMB} Mo, mais la limite est de 2 Mo.\n\n` +
          `Pour de meilleurs résultats, utilise un fichier de 10 pages max.\n\n` +
          `💡 Astuce : découpe ton cours en plusieurs parties !`
        );
      } else {
        Alert.alert(
          '📄 Fichier trop volumineux',
          `Ton document fait ${fileSizeMB} Mo, mais la limite est de 2 Mo.\n\nPour de meilleurs résultats, utilise un fichier de 10 pages max.\n\n💡 Astuce : découpe ton cours en plusieurs parties !`,
          [{ text: 'OK, j\'ai compris', style: 'default' }]
        );
      }
      return;
    }
    
    if (!result.uri) return;

    const uploadResult = await uploadAndGenerate(result.uri, selectedIntent, fileName);

    if (uploadResult.success) {
      handleGenerationSuccess(uploadResult);
    } else {
      if (uploadResult.error?.startsWith('FILE_TOO_LARGE:')) {
        const fileSizeMB = uploadResult.error.split(':')[1] || '?';
        
        if (isWeb) {
          window.alert(
            `📄 Fichier trop volumineux\n\n` +
            `Ton document fait ${fileSizeMB} Mo, mais la limite est de 2 Mo.\n\n` +
            `Pour de meilleurs résultats, utilise un fichier de 10 pages max.\n\n` +
            `💡 Astuce : découpe ton cours en plusieurs parties !`
          );
        } else {
          Alert.alert(
            '📄 Fichier trop volumineux',
            `Ton document fait ${fileSizeMB} Mo, mais la limite est de 2 Mo.\n\nPour de meilleurs résultats, utilise un fichier de 10 pages max.\n\n💡 Astuce : découpe ton cours en plusieurs parties !`,
            [{ text: 'OK, j\'ai compris', style: 'default' }]
          );
        }
      } else {
        showErrorAlert(uploadResult.error || 'Une erreur est survenue', uploadResult.errorCode);
      }
    }
  };

  const getIntentLabel = (intent: ContentIntent) => {
    switch (intent) {
      case 'fiche': return 'une fiche';
      case 'quiz': return 'un quiz';
      case 'flashcards': return 'des flashcards';
    }
  };

  const getIntentIcon = (intent: ContentIntent, size: number = 24) => {
    switch (intent) {
      case 'fiche': 
        return <FileText size={size} color={colors.indigo600} weight="duotone" />;
      case 'quiz': 
        return <Question size={size} color={colors.amber500} weight="duotone" />;
      case 'flashcards': 
        return <Cards size={size} color={colors.emerald500} weight="duotone" />;
    }
  };

  const getIntentBgColor = (intent: ContentIntent) => {
    switch (intent) {
      case 'fiche': return colors.indigo100;
      case 'quiz': return colors.amber100;
      case 'flashcards': return colors.emerald100;
    }
  };

  const getIntentColor = (intent: ContentIntent) => {
    switch (intent) {
      case 'fiche': return colors.indigo600;
      case 'quiz': return colors.amber500;
      case 'flashcards': return colors.emerald500;
    }
  };

  const getSuccessMessage = (intent: ContentIntent) => {
    switch (intent) {
      case 'fiche': return 'Fiche créée !';
      case 'quiz': return 'Quiz créé !';
      case 'flashcards': return 'Flashcards créées !';
    }
  };

  const getXpValue = (intent: ContentIntent) => {
    return XP_VALUES[intent];
  };

  const getQuotaDisplay = (intent: ContentIntent) => {
    if (isPremium) return null;
    
    const usage = intent === 'fiche' ? monthlyUsage.fiches : 
                  intent === 'quiz' ? monthlyUsage.quiz : 
                  monthlyUsage.flashcards;
    const limit = intent === 'fiche' ? FREE_LIMITS.fiches :
                  intent === 'quiz' ? FREE_LIMITS.quiz :
                  FREE_LIMITS.flashcards;
    
    return `${usage}/${limit}`;
  };

  const getBottomPadding = () => {
    if (isWeb) return 24;
    return Math.max(insets.bottom, 20) + 16;
  };

  // ✅ Rendu des étapes avec ActivityIndicator fiable
  const renderStepsProgress = () => {
    const steps = getStepsFromProgress(progress, selectedIntent);

    return (
      <View style={styles.stepsContainer}>
        {steps.map((step) => (
          <View key={step.id} style={styles.stepRow}>
            <View style={styles.stepIconContainer}>
              {step.status === 'completed' ? (
                <View style={styles.stepIconCompleted}>
                  <Text style={styles.stepCheckText}>✓</Text>
                </View>
              ) : step.status === 'active' ? (
                <ActivityIndicator size={20} color={colors.indigo600} />
              ) : (
                <View style={styles.stepIconPending} />
              )}
            </View>
            <Text style={[
              styles.stepLabel,
              step.status === 'completed' && styles.stepLabelCompleted,
              step.status === 'active' && styles.stepLabelActive,
              step.status === 'pending' && styles.stepLabelPending,
            ]}>
              {step.label}
            </Text>
          </View>
        ))}
      </View>
    );
  };

  // ✅ Rendu de l'écran de chargement avec étapes + barre de progression %
  const renderLoadingWithSteps = () => {
    const roundedPercent = Math.round(displayPercent);
    
    return (
      <View style={styles.loadingWithStepsContainer}>
        <Text style={styles.loadingTitle}>En cours de création…</Text>
        
        {renderStepsProgress()}
        
        {/* ✅ Barre de progression avec % */}
        <View style={styles.progressBarContainer}>
          <View style={styles.progressBarTrack}>
            <LinearGradient
              colors={[colors.indigo600, colors.purple600]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              style={[styles.progressBarFill, { width: `${Math.min(roundedPercent, 100)}%` }]}
            />
          </View>
          <Text style={styles.progressPercentText}>{roundedPercent}%</Text>
        </View>

        <View style={styles.loadingFooter}>
          <Text style={styles.loadingFooterText}>
            {roundedPercent < 20 
              ? 'Préparation en cours…'
              : roundedPercent < 50 
                ? 'Analyse de ton document…'
                : roundedPercent < 85
                  ? 'L\'IA rédige ton contenu…'
                  : roundedPercent < 100
                    ? 'Presque terminé !'
                    : 'C\'est prêt !'
            }
          </Text>
        </View>
      </View>
    );
  };

  const renderRenameStep = () => (
    <ScrollView 
      contentContainerStyle={[
        styles.renameScrollContent,
        { paddingBottom: getBottomPadding() }
      ]}
      keyboardShouldPersistTaps="handled"
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.renameContainer}>
        <View style={[styles.renameIconContainer, { backgroundColor: getIntentBgColor(selectedIntent) }]}>
          {getIntentIcon(selectedIntent, 36)}
        </View>
        
        <Text style={styles.renameTitle}>
          {selectedIntent === 'fiche' ? 'Fiche générée !' : 
           selectedIntent === 'quiz' ? 'Quiz généré !' : 
           'Flashcards générées !'}
        </Text>
        <Text style={styles.renameSubtitle}>Donne un nom pour la retrouver facilement</Text>
        
        <TextInput
          style={styles.renameInput}
          value={customTitle}
          onChangeText={setCustomTitle}
          placeholder="Ex: Chapitre 3 - Les fonctions"
          placeholderTextColor={colors.slate400}
          autoFocus={!isWeb}
          selectTextOnFocus
        />
        
        <TouchableOpacity 
          style={[styles.renameButton, !customTitle.trim() && styles.renameButtonDisabled]}
          onPress={saveTitle}
          disabled={!customTitle.trim() || isSavingTitle}
          activeOpacity={0.8}
        >
          <LinearGradient
            colors={!customTitle.trim() ? [colors.slate300, colors.slate300] : [colors.indigo600, colors.purple600]}
            style={styles.renameButtonGradient}
          >
            {isSavingTitle ? (
              <ActivityIndicator size="small" color={colors.white} />
            ) : (
              <Text style={styles.renameButtonText}>Enregistrer</Text>
            )}
          </LinearGradient>
        </TouchableOpacity>
        
        <TouchableOpacity style={styles.skipButton} onPress={skipRename}>
          <Text style={styles.skipButtonText}>Garder le titre par défaut</Text>
        </TouchableOpacity>
        
        {xpData && (
          <View style={styles.xpPreview}>
            <View style={styles.xpPreviewBadge}>
              <Text style={styles.xpPreviewText}>+{xpData.xpAwarded} XP</Text>
            </View>
            {xpData.newStreak > 0 && (
              <View style={styles.streakPreviewBadge}>
                <Text style={styles.streakPreviewText}>🔥 {xpData.newStreak}j</Text>
              </View>
            )}
          </View>
        )}
      </View>
    </ScrollView>
  );

  const renderPhotoGrid = () => (
    <View style={[styles.photoModeContainer, { paddingBottom: getBottomPadding() }]}>
      <View style={styles.photoModeHeader}>
        <TouchableOpacity onPress={() => setShowPhotoMode(false)} style={styles.backButton}>
          <Text style={styles.backButtonText}>← Retour</Text>
        </TouchableOpacity>
        <Text style={styles.photoModeTitle}>📸 Photos du cours</Text>
      </View>

      <Text style={styles.photoModeSubtitle}>
        {photos.length === 0 
          ? 'Prends en photo les pages de ton cours'
          : `${photos.length} photo${photos.length > 1 ? 's' : ''} ajoutée${photos.length > 1 ? 's' : ''}`
        }
      </Text>

      <ScrollView style={styles.photoScrollView} contentContainerStyle={styles.photoGrid}>
        {photos.map((photo) => (
          <View key={photo.id} style={styles.photoItem}>
            <Image source={{ uri: photo.uri }} style={styles.photoThumbnail} />
            <TouchableOpacity 
              style={styles.removePhotoButton}
              onPress={() => removePhoto(photo.id)}
            >
              <X size={12} color={colors.white} weight="bold" />
            </TouchableOpacity>
          </View>
        ))}
        
        <TouchableOpacity 
          style={styles.addPhotoButton}
          onPress={() => handleAddPhoto(true)}
        >
          <Camera size={28} color={colors.indigo600} weight="duotone" />
          <Text style={styles.addPhotoText}>Ajouter</Text>
        </TouchableOpacity>
      </ScrollView>

      <View style={styles.photoModeActions}>
        <TouchableOpacity 
          style={styles.galleryButton}
          onPress={() => handleAddPhoto(false)}
        >
          <Images size={20} color={colors.slate600} weight="duotone" />
          <Text style={styles.galleryButtonText}>Galerie</Text>
        </TouchableOpacity>

        <TouchableOpacity 
          style={[
            styles.generateButton,
            photos.length === 0 && styles.generateButtonDisabled
          ]}
          onPress={handleGenerateFromPhotos}
          disabled={photos.length === 0}
          activeOpacity={0.8}
        >
          <LinearGradient
            colors={photos.length === 0 ? [colors.slate300, colors.slate300] : [colors.indigo600, colors.purple600]}
            style={styles.generateButtonGradient}
          >
            <Text style={styles.generateButtonText}>
              Générer →
            </Text>
          </LinearGradient>
        </TouchableOpacity>
      </View>
    </View>
  );

  return (
    <Modal
      visible={visible}
      animationType={isWeb ? "fade" : "slide"}
      transparent={true}
      onRequestClose={handleClose}
    >
      <KeyboardAvoidingView 
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardAvoid}
      >
        <View style={styles.overlay}>
          {isWeb && (
            <TouchableOpacity 
              style={StyleSheet.absoluteFill} 
              activeOpacity={1} 
              onPress={handleClose}
            />
          )}
          
          <View style={styles.modalWrapper}>
            <View style={[
              styles.modal,
              showRenameStep && styles.modalRename,
              { paddingBottom: getBottomPadding() }
            ]}>
            {showSuccess && xpData ? (
              <Animated.View 
                style={[
                  styles.successContainer,
                  {
                    opacity: opacityAnim,
                    transform: [{ scale: scaleAnim }],
                  }
                ]}
              >
                <View style={[styles.successIconContainer, { backgroundColor: getIntentBgColor(selectedIntent) }]}>
                  {getIntentIcon(selectedIntent, 48)}
                </View>
                
                <Text style={styles.successTitle}>{getSuccessMessage(selectedIntent)}</Text>
                <Text style={styles.successFeedback}>{xpData.feedbackMessage}</Text>
                
                <View style={styles.xpBadgeContainer}>
                  <LinearGradient
                    colors={[colors.indigo600, colors.purple600]}
                    style={styles.xpBadge}
                  >
                    <Text style={styles.xpBadgeText}>+{xpData.xpAwarded} XP</Text>
                  </LinearGradient>
                  
                  {xpData.newStreak > 0 && (
                    <View style={styles.streakBadge}>
                      <Text style={styles.streakBadgeText}>🔥 {xpData.newStreak}j</Text>
                    </View>
                  )}
                </View>

                {xpData.leveledUp && (
                  <View style={styles.levelUpContainer}>
                    <Text style={styles.levelUpText}>🎉 Niveau supérieur !</Text>
                  </View>
                )}

                <Text style={styles.successHint}>Redirection en cours...</Text>
              </Animated.View>
            ) : showRenameStep ? (
              renderRenameStep()
            ) : showPhotoMode ? (
              isLoading ? (
                renderLoadingWithSteps()
              ) : (
                renderPhotoGrid()
              )
            ) : (
              <>
                <View style={styles.header}>
                  <View style={styles.headerLeft}>
                    <View style={[styles.headerIconContainer, { backgroundColor: getIntentBgColor(selectedIntent) }]}>
                      {getIntentIcon(selectedIntent, 22)}
                    </View>
                    <View style={styles.headerTitleContainer}>
                      <Text style={styles.headerTitle}>Créer {getIntentLabel(selectedIntent)}</Text>
                      <View style={styles.headerBadges}>
                        <View style={[styles.headerXpBadge, { backgroundColor: getIntentBgColor(selectedIntent) }]}>
                          <Text style={[styles.headerXpText, { color: getIntentColor(selectedIntent) }]}>
                            +{getXpValue(selectedIntent)} XP
                          </Text>
                        </View>
                        {!isPremium && getQuotaDisplay(selectedIntent) && (
                          <Text style={styles.headerQuotaText}>{getQuotaDisplay(selectedIntent)}</Text>
                        )}
                      </View>
                    </View>
                  </View>
                  {!isLoading && (
                    <TouchableOpacity onPress={handleClose} style={styles.closeButton}>
                      <X size={20} color={colors.slate500} weight="bold" />
                    </TouchableOpacity>
                  )}
                </View>

                {isLoading ? (
                  renderLoadingWithSteps()
                ) : (
                  <View style={styles.content}>
                    <Text style={styles.subtitle}>Choisis la source de ton cours</Text>
                    
                    <TouchableOpacity
                      style={styles.sourceCard}
                      onPress={() => handleSourceSelect('photo')}
                      activeOpacity={0.7}
                    >
                      <View style={[styles.sourceIconCircle, { backgroundColor: colors.indigo100 }]}>
                        <Camera size={24} color={colors.indigo600} weight="duotone" />
                      </View>
                      <View style={styles.sourceInfo}>
                        <Text style={styles.sourceTitle}>Prendre en photo</Text>
                        <Text style={styles.sourceDescription}>Une ou plusieurs pages de cours</Text>
                      </View>
                      <Text style={styles.chevron}>›</Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={styles.sourceCard}
                      onPress={() => handleSourceSelect('document')}
                      activeOpacity={0.7}
                    >
                      <View style={[styles.sourceIconCircle, { backgroundColor: colors.purple100 }]}>
                        <File size={22} color={colors.purple600} weight="duotone" />
                      </View>
                      <View style={styles.sourceInfo}>
                        <Text style={styles.sourceTitle}>Document PDF</Text>
                        <Text style={styles.sourceDescription}>Importer un fichier PDF (2 Mo · 10 pages max)</Text>
                      </View>
                      <Text style={styles.chevron}>›</Text>
                    </TouchableOpacity>
                  </View>
                )}

                {error && (
                  <View style={styles.errorContainer}>
                    <Text style={styles.errorText}>⚠️ {error}</Text>
                  </View>
                )}
              </>
            )}
            </View>
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
}

const styles = StyleSheet.create({
  keyboardAvoid: {
    flex: 1,
  },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(15, 23, 42, 0.6)',
    justifyContent: isWeb ? 'center' : 'flex-end',
    alignItems: isWeb ? 'center' : undefined,
    padding: isWeb ? 20 : 0,
  },
  modalWrapper: {
    width: '100%',
    maxWidth: isWeb ? MAX_WIDTH : undefined,
  },
  modal: {
    backgroundColor: colors.white,
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    ...(isWeb && {
      borderBottomLeftRadius: 28,
      borderBottomRightRadius: 28,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 10 },
      shadowOpacity: 0.25,
      shadowRadius: 30,
      elevation: 20,
    }),
    width: '100%',
    maxHeight: isWeb ? undefined : SCREEN_HEIGHT * 0.92,
  },
  modalRename: {
    maxHeight: isWeb ? '80%' : SCREEN_HEIGHT * 0.88,
  },
  
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: colors.slate200,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  headerIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitleContainer: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.slate900,
  },
  headerBadges: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 4,
  },
  headerXpBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
  },
  headerXpText: {
    fontSize: 12,
    fontWeight: '700',
  },
  headerQuotaText: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.slate400,
  },
  closeButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: colors.slate100,
    alignItems: 'center',
    justifyContent: 'center',
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  
  content: {
    padding: 20,
    paddingBottom: 8,
  },
  subtitle: {
    fontSize: 15,
    color: colors.slate500,
    marginBottom: 20,
  },
  
  sourceCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.white,
    borderRadius: 16,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: colors.slate200,
    shadowColor: colors.slate900,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 8,
    elevation: 2,
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  sourceIconCircle: {
    width: 48,
    height: 48,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
  },
  sourceInfo: {
    flex: 1,
  },
  sourceTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.slate900,
    marginBottom: 4,
  },
  sourceDescription: {
    fontSize: 13,
    color: colors.slate500,
    lineHeight: 18,
  },
  chevron: {
    fontSize: 24,
    color: colors.slate400,
    marginLeft: 8,
  },
  
  loadingContainer: {
    padding: 40,
    alignItems: 'center',
  },
  loadingIconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  loadingText: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.slate900,
    textAlign: 'center',
  },
  loadingHint: {
    fontSize: 13,
    color: colors.slate500,
    marginTop: 8,
    textAlign: 'center',
  },
  
  loadingWithStepsContainer: {
    padding: 24,
    paddingTop: 32,
    paddingBottom: 32,
  },
  loadingTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.slate900,
    textAlign: 'center',
    marginBottom: 28,
  },
  stepsContainer: {
    gap: 16,
  },
  stepRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.slate50,
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 12,
  },
  stepIconContainer: {
    width: 28,
    height: 28,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  stepIconCompleted: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: colors.emerald500,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepCheckText: {
    color: colors.white,
    fontSize: 14,
    fontWeight: '700',
  },
  stepIconPending: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: colors.slate300,
    backgroundColor: 'transparent',
  },
  stepLabel: {
    fontSize: 15,
    fontWeight: '500',
    flex: 1,
  },
  stepLabelCompleted: {
    color: colors.emerald500,
    fontWeight: '600',
  },
  stepLabelActive: {
    color: colors.indigo600,
    fontWeight: '600',
  },
  stepLabelPending: {
    color: colors.slate400,
  },

  // ✅ Barre de progression
  progressBarContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 24,
    gap: 12,
  },
  progressBarTrack: {
    flex: 1,
    height: 8,
    backgroundColor: colors.slate200,
    borderRadius: 4,
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    borderRadius: 4,
  },
  progressPercentText: {
    fontSize: 15,
    fontWeight: '700',
    color: colors.indigo600,
    minWidth: 42,
    textAlign: 'right',
  },

  loadingFooter: {
    marginTop: 12,
    alignItems: 'center',
  },
  loadingFooterText: {
    fontSize: 13,
    color: colors.slate400,
    textAlign: 'center',
  },
  
  errorContainer: {
    backgroundColor: colors.rose100,
    padding: 16,
    marginHorizontal: 20,
    borderRadius: 12,
    marginTop: 8,
  },
  errorText: {
    fontSize: 14,
    color: colors.rose500,
  },
  
  renameScrollContent: {
    flexGrow: 1,
  },
  renameContainer: {
    padding: 24,
    alignItems: 'center',
  },
  renameIconContainer: {
    width: 70,
    height: 70,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  renameTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: colors.slate900,
    marginBottom: 8,
  },
  renameSubtitle: {
    fontSize: 15,
    color: colors.slate500,
    marginBottom: 24,
    textAlign: 'center',
  },
  renameInput: {
    width: '100%',
    backgroundColor: colors.slate50,
    borderWidth: 2,
    borderColor: colors.slate200,
    borderRadius: 14,
    padding: 16,
    fontSize: 16,
    color: colors.slate900,
    marginBottom: 16,
  },
  renameButton: {
    width: '100%',
    borderRadius: 14,
    overflow: 'hidden',
    marginBottom: 12,
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  renameButtonDisabled: {
    opacity: 1,
  },
  renameButtonGradient: {
    paddingVertical: 16,
    alignItems: 'center',
  },
  renameButtonText: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.white,
  },
  skipButton: {
    padding: 12,
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  skipButtonText: {
    fontSize: 14,
    color: colors.slate500,
  },
  xpPreview: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 16,
  },
  xpPreviewBadge: {
    backgroundColor: colors.indigo100,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 10,
  },
  xpPreviewText: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.indigo600,
  },
  streakPreviewBadge: {
    backgroundColor: colors.amber100,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 10,
  },
  streakPreviewText: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.amber500,
  },

  successContainer: {
    padding: 40,
    alignItems: 'center',
  },
  successIconContainer: {
    width: 100,
    height: 100,
    borderRadius: 50,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
    shadowColor: colors.indigo600,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.2,
    shadowRadius: 16,
    elevation: 8,
  },
  successTitle: {
    fontSize: 24,
    fontWeight: '800',
    color: colors.slate900,
    marginBottom: 8,
  },
  successFeedback: {
    fontSize: 16,
    color: colors.slate500,
    marginBottom: 24,
  },
  xpBadgeContainer: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16,
  },
  xpBadge: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 20,
    shadowColor: colors.indigo600,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 4,
  },
  xpBadgeText: {
    color: colors.white,
    fontSize: 18,
    fontWeight: '800',
  },
  streakBadge: {
    backgroundColor: colors.amber500,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 20,
    shadowColor: colors.amber500,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 4,
  },
  streakBadgeText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: '700',
  },
  levelUpContainer: {
    backgroundColor: colors.amber100,
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 16,
    marginBottom: 16,
  },
  levelUpText: {
    color: colors.amber500,
    fontSize: 16,
    fontWeight: '700',
  },
  successHint: {
    fontSize: 13,
    color: colors.slate400,
    marginTop: 8,
  },
  
  photoModeContainer: {
    padding: 20,
  },
  photoModeHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  backButton: {
    marginRight: 12,
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  backButtonText: {
    fontSize: 16,
    color: colors.indigo600,
    fontWeight: '600',
  },
  photoModeTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.slate900,
  },
  photoModeSubtitle: {
    fontSize: 14,
    color: colors.slate500,
    marginBottom: 20,
  },
  photoScrollView: {
    maxHeight: 280,
  },
  photoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  photoItem: {
    width: 100,
    height: 100,
    borderRadius: 12,
    overflow: 'hidden',
    position: 'relative',
  },
  photoThumbnail: {
    width: '100%',
    height: '100%',
  },
  removePhotoButton: {
    position: 'absolute',
    top: 6,
    right: 6,
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: colors.rose500,
    alignItems: 'center',
    justifyContent: 'center',
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  addPhotoButton: {
    width: 100,
    height: 100,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: colors.indigo200,
    borderStyle: 'dashed',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.indigo50,
    gap: 4,
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  addPhotoText: {
    fontSize: 12,
    color: colors.indigo600,
    fontWeight: '500',
  },
  photoModeActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 20,
  },
  galleryButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    borderRadius: 14,
    backgroundColor: colors.slate100,
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  galleryButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.slate600,
  },
  generateButton: {
    flex: 2,
    borderRadius: 14,
    overflow: 'hidden',
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  generateButtonDisabled: {
    opacity: 1,
  },
  generateButtonGradient: {
    paddingVertical: 14,
    alignItems: 'center',
  },
  generateButtonText: {
    fontSize: 15,
    fontWeight: '700',
    color: colors.white,
  },
});
